import { toVariablesList } from '@/utils/emailComposition';
import { isEmailBodyEmpty } from '@/utils/emailComposition';
import { useCallback, useMemo, useRef } from 'react';
import type { FormikProps } from 'formik';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { useGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import { checkForInvalidVariablesInSubjectAndBody } from '@/utils/commonHelpers';
import type { AutoRejectEmailSettings } from '@/api/aiScoring/aiScoring.api';
import {
  REJECTION_EMAIL_VARIABLES_VIEW,
  type AutoRejectEmailFormValues,
} from '../autoRejectEmail.types';
import {
  mapConfigToFormValues,
  mapFormValuesToEmailSettings,
  type AutoRejectEmailConfigResponse,
} from './autoRejectEmailPayload';

/** Errors shown when the rejection email is enabled but incomplete. */
export const TEMPLATE_REQUIRED_ERROR = 'Select a rejection email template';
export const FROM_REQUIRED_ERROR = 'From email is required';
export const SUBJECT_REQUIRED_ERROR = 'Email subject is required';
export const BODY_REQUIRED_ERROR = 'Email body is required';
export const INVALID_VARIABLES_ERROR =
  'Empty variables detected in the email. Remove them to continue';

/**
 * Shared glue for the auto-reject rejection-email island across the Scoring
 * agent surfaces (setup wizard, screener info view, Job Information edit view).
 * Owns the formRef the `AutoRejectEmailSection` writes to, derives restore
 * `initialValues` from the persisted config, and builds the save payload.
 */
export function useAutoRejectEmail(
  aiScoring?: AutoRejectEmailConfigResponse | null
) {
  const formRef = useRef<FormikProps<AutoRejectEmailFormValues> | null>(null);
  const { fromUsers } = useGetAllUsers();

  // Same query/view the email editor uses for its variable picker and the
  // "Empty variables detected" banner, so save-time validation and the banner
  // agree on which {{variables}} are valid.
  const { data: { variables = {} } = {} } = useGetTemplatesVariablesQuery({
    params: { view: REJECTION_EMAIL_VARIABLES_VIEW },
  });
  const variablesListArr = useMemo(
    () => toVariablesList(variables),
    [variables]
  );

  const initialValues = useMemo(
    () => mapConfigToFormValues(aiScoring, fromUsers),
    [aiScoring, fromUsers]
  );

  // Reads the live form values at save time; falls back to the restored values
  // if the section hasn't mounted (e.g. auto-reject disabled).
  const buildEmailSettings = useCallback((): AutoRejectEmailSettings => {
    const values = formRef.current?.values ?? initialValues;
    return mapFormValuesToEmailSettings(values);
  }, [initialValues]);

  // Returns a validation error (or null). Once the user opts into sending a
  // rejection email, a template must be selected, the From sender + subject
  // + body (prefilled from the template, editable) must be non-empty, and the
  // subject/body must not contain unresolvable {{variables}} (the same check
  // behind the "Empty variables detected" banner). The From check mirrors the
  // fraud-detector `computeIsSaveEnabled` (`values.from`).
  const validateEmailConfig = useCallback((): string | null => {
    const values = formRef.current?.values ?? initialValues;
    if (!values.sendRejectEmail) return null;
    const template = values.selectedTemplate as { id?: number } | null;
    if (!template?.id) return TEMPLATE_REQUIRED_ERROR;
    if (!values.from?.trim()) return FROM_REQUIRED_ERROR;
    if (!values.subject?.trim()) return SUBJECT_REQUIRED_ERROR;
    if (isEmailBodyEmpty(values.body)) return BODY_REQUIRED_ERROR;
    // Skip until the variables list has loaded — with an empty list every
    // {{variable}} would count as invalid and wrongly block the save.
    if (
      variablesListArr.length > 0 &&
      checkForInvalidVariablesInSubjectAndBody({
        variablesListArr,
        subject: values.subject,
        body: values.body,
        // The banner excludes the signature variable from the subject scope.
        variablesListSubject: variablesListArr.filter(
          (variable) => variable !== 'sender.email_signature'
        ),
      })
    ) {
      return INVALID_VARIABLES_ERROR;
    }
    return null;
  }, [initialValues, variablesListArr]);

  return { formRef, initialValues, buildEmailSettings, validateEmailConfig };
}
